import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Typography,
    Grid,
    Chip,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
} from '@mui/material';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ICompany } from '../../../redux/company/types';
import { SERVICE } from '../../../data/forms/company.data';
import { Add } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { updateCompany } from '../../../redux/company/actions';

interface ICompanyServicesEdit {
    company?: ICompany;
    isEditing: boolean;
    onSave: (saveFunction: () => void) => void;
    onChanges: (hasChanges: boolean) => void;
}

const CompanyServicesEdit: React.FC<ICompanyServicesEdit> = ({ company, isEditing, onSave, onChanges }) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const [formData, setFormData] = useState({
        service: company?.service || 'Media services',
        other_services: company?.other_services || [],
    });
    const [initialData, setInitialData] = useState(formData);
    const [newService, setNewService] = useState('');

    useEffect(() => {
        if (company) {
            const newData = {
                service: company.service || 'Media services',
                other_services: company.other_services || [],
            };
            setFormData(newData);
            setInitialData(newData);
        }
    }, [company]);

    useEffect(() => {
        const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialData);
        onChanges(hasChanges);
    }, [formData, initialData, onChanges]);

    const handleFieldChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const addService = () => {
        if (newService.trim() && !formData.other_services.includes(newService.trim())) {
            handleFieldChange('other_services', [...formData.other_services, newService.trim()]);
            setNewService('');
        }
    };

    const removeService = (serviceToRemove: string) => {
        handleFieldChange(
            'other_services',
            formData.other_services.filter(service => service !== serviceToRemove),
        );
    };

    const handleSave = () => {
        if (company?._id) {
            const payload = {
                id: company._id,
                body: {
                    service: formData.service,
                    other_services: formData.other_services,
                },
            };
            dispatch(updateCompany(payload));
        }
    };

    // Register the save function with the parent container
    useEffect(() => {
        onSave(handleSave);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSave, formData, company?._id]);

    return (
        <Card sx={{ bgcolor: theme[themeStyle].secondaryBg, border: `1px solid ${theme[themeStyle].tag_white_light}` }}>
            <CardHeader
                title="Services & Industry"
                sx={{ color: theme[themeStyle].tag_white }}
                titleTypographyProps={{
                    sx: { fontFamily: FONT_ENUM.MAIN_BOLD },
                }}
            />
            <CardContent>
                <Grid container spacing={3}>
                    {/* Primary Service */}
                    <Grid item xs={12} md={6}>
                        <Typography
                            variant="h6"
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                mb: 2,
                            }}>
                            Primary Service
                        </Typography>
                        {isEditing ? (
                            <FormControl fullWidth>
                                <InputLabel sx={{ color: theme[themeStyle].tag_white }}>Primary Service</InputLabel>
                                <Select
                                    value={formData.service}
                                    onChange={e => handleFieldChange('service', e.target.value)}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].dropdown_black,
                                                border: `1px solid ${theme[themeStyle].form_divider}`,
                                                borderRadius: '8px',
                                                maxHeight: 300,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].primary_white,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].form_divider,
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme[themeStyle].highlight,
                                                        color: theme[themeStyle].primary_white,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].highlight,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    }}
                                    sx={{
                                        '& .MuiSelect-select': {
                                            color: theme[themeStyle].tag_white,
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].primary_white,
                                        },
                                    }}>
                                    {SERVICE.map(service => (
                                        <MenuItem key={service} value={service}>
                                            {service}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <Chip
                                label={formData.service}
                                sx={{
                                    backgroundColor: theme[themeStyle].highlight,
                                    color: theme[themeStyle].black,
                                    fontSize: '1rem',
                                    padding: '8px 16px',
                                }}
                            />
                        )}
                    </Grid>

                    {/* Other Services */}
                    <Grid item xs={12}>
                        <Typography
                            variant="h6"
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                mb: 2,
                            }}>
                            Additional Services
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                            {formData.other_services.map(service => (
                                <Chip
                                    key={service}
                                    label={service}
                                    onDelete={isEditing ? () => removeService(service) : undefined}
                                    sx={{
                                        backgroundColor: theme[themeStyle].highlight,
                                        color: theme[themeStyle].black,
                                        '& .MuiChip-deleteIcon': {
                                            color: theme[themeStyle].black,
                                        },
                                    }}
                                />
                            ))}
                        </Box>
                        {isEditing && (
                            <Box display="flex" gap={1}>
                                <TextField
                                    value={newService}
                                    onChange={e => setNewService(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && addService()}
                                    label="Add Service"
                                    InputLabelProps={{
                                        sx: {
                                            color: theme[themeStyle].grey,
                                        },
                                    }}
                                    fullWidth
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: theme[themeStyle].grey,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme[themeStyle].highlight,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme[themeStyle].highlight,
                                            },
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].primary_white,
                                        },
                                    }}
                                    InputProps={{
                                        style: {
                                            background: theme[themeStyle].gray_text,
                                            color: theme[themeStyle].lightWhite,
                                            borderRadius: 8,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            border: `1px solid ${theme[themeStyle].form_gray}`,
                                        },
                                    }}
                                />
                                <IconButton onClick={addService}>
                                    <Add sx={{ color: theme[themeStyle].tag_white }} />
                                </IconButton>
                            </Box>
                        )}
                        {!isEditing && formData.other_services.length === 0 && (
                            <Typography
                                sx={{
                                    color: theme[themeStyle].lightWhite,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontStyle: 'italic',
                                }}>
                                No additional services specified
                            </Typography>
                        )}
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export { CompanyServicesEdit };
